import { useEffect } from 'react';
import { create } from 'zustand';

import { NAV_STORAGE_KEY } from '@shared/theme/tokens';

/**
 * The navigation drawer's open flag.
 *
 * Area-scoped by design: an operator's console navigation and a viewer's
 * dashboard navigation are different navigations, and letting one collapse the
 * other is a state leak. The theme key is shared across the three hosts; this
 * one is not.
 *
 * The flag is additionally **cleared on every crossing into the desktop tier**,
 * so returning below it never re-opens a drawer nobody asked for.
 */

type NavFlag = 'open' | 'closed';

function readStoredFlag(): boolean {
  try {
    return window.localStorage.getItem(NAV_STORAGE_KEY) === 'open';
  } catch {
    return false;
  }
}

function writeStoredFlag(open: boolean): void {
  try {
    window.localStorage.setItem(NAV_STORAGE_KEY, (open ? 'open' : 'closed') satisfies NavFlag);
  } catch {
    // Persisting is a convenience; rendering is not.
  }
}

interface NavState {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

export const useNavStore = create<NavState>((set, get) => ({
  open: false,
  setOpen: (open) => {
    writeStoredFlag(open);
    set({ open });
  },
  toggle: () => get().setOpen(!get().open),
}));

/** Reads the desktop threshold off the token file rather than restating it. */
function desktopQuery(): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue('--bp-lg').trim();
  return `(min-width: ${value})`;
}

export function useNavTierSync(): void {
  useEffect(() => {
    useNavStore.setState({ open: readStoredFlag() });

    const media = window.matchMedia(desktopQuery());

    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      // Crossing into the desktop tier closes the drawer: from `--bp-lg` up the
      // same list is a permanent column and there is no drawer to be open.
      if (event.matches) useNavStore.getState().setOpen(false);
    };

    handleChange(media);
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);
}
